// src/services/productionSeedData.ts
//
// Sample dataset adapted from the GeniBatch standalone app's seedData.js.
// Trimmed to match the Phase 1+2 model (no nutrition / allergens / labor —
// those land in Phase 3). Tenant-scoped: each tenant gets its own copy on
// first load, with a per-tenant "already seeded" flag so we don't clobber
// the user's data on subsequent loads.

import type {
  ProductionItem, ProductionRecipe, ProductionProduct,
} from '../types/production';

const NOW = '2026-01-01T00:00:00.000Z';

// ─── Items (raw materials + packaging) ────────────────────────────────────

export const SEED_ITEMS: ProductionItem[] = [
  // Produce — nutrition is per 100g, no allergens
  { id: 'item-001', name: 'Tomato', category: 'Produce', unit: 'lb', costPerUnit: 1.49, nutrition: { kcal: 18, protein: 0.9, fat: 0.2, carbs: 3.9, fiber: 1.2, sugar: 2.6, sodium: 5, potassium: 237 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-002', name: 'Cucumber', category: 'Produce', unit: 'lb', costPerUnit: 0.99, nutrition: { kcal: 15, protein: 0.7, fat: 0.1, carbs: 3.6, fiber: 0.5, sugar: 1.7, sodium: 2, potassium: 147 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-003', name: 'Red Onion', category: 'Produce', unit: 'lb', costPerUnit: 1.29, nutrition: { kcal: 40, protein: 1.1, fat: 0.1, carbs: 9.3, fiber: 1.7, sugar: 4.2, sodium: 4, potassium: 146 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-004', name: 'Avocado', category: 'Produce', unit: 'ea', costPerUnit: 1.50, description: 'Avg 150g each', nutrition: { kcal: 160, protein: 2, fat: 14.7, satfat: 2.1, carbs: 8.5, fiber: 6.7, sugar: 0.7, sodium: 7, potassium: 485 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-005', name: 'Lemon', category: 'Produce', unit: 'ea', costPerUnit: 0.50, nutrition: { kcal: 29, protein: 1.1, fat: 0.3, carbs: 9.3, fiber: 2.8, sugar: 2.5, sodium: 2, potassium: 138 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-006', name: 'Green Pepper', category: 'Produce', unit: 'lb', costPerUnit: 1.29, nutrition: { kcal: 20, protein: 0.9, fat: 0.2, carbs: 4.6, fiber: 1.7, sugar: 2.4, sodium: 3, potassium: 175 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-007', name: 'Beet', category: 'Produce', unit: 'lb', costPerUnit: 1.99, nutrition: { kcal: 43, protein: 1.6, fat: 0.2, carbs: 9.6, fiber: 2.8, sugar: 6.8, sodium: 78, potassium: 325 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-008', name: 'Eggplant', category: 'Produce', unit: 'lb', costPerUnit: 1.79, nutrition: { kcal: 25, protein: 1, fat: 0.2, carbs: 5.9, fiber: 3, sugar: 3.5, sodium: 2, potassium: 229 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-009', name: 'Garlic', category: 'Produce', unit: 'lb', costPerUnit: 4.99, nutrition: { kcal: 149, protein: 6.4, fat: 0.5, satfat: 0.1, carbs: 33.1, fiber: 2.1, sugar: 1, sodium: 17, potassium: 401 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-010', name: 'Parsley', category: 'Produce', unit: 'ea', costPerUnit: 0.99, description: 'Bunch ~60g', nutrition: { kcal: 36, protein: 3, fat: 0.8, carbs: 6.3, fiber: 3.3, sodium: 56, potassium: 554 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-011', name: 'Mint', category: 'Produce', unit: 'ea', costPerUnit: 1.29, description: 'Bunch ~30g', nutrition: { kcal: 44, protein: 3.3, fat: 0.7, carbs: 8.4, fiber: 6.8, sodium: 30, potassium: 458 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-012', name: 'Cilantro', category: 'Produce', unit: 'ea', costPerUnit: 0.89, description: 'Bunch ~50g', nutrition: { kcal: 23, protein: 2.1, fat: 0.5, carbs: 3.7, fiber: 2.8, sugar: 0.9, sodium: 46, potassium: 521 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-013', name: 'Jalapeño', category: 'Produce', unit: 'lb', costPerUnit: 2.49, nutrition: { kcal: 29, protein: 0.9, fat: 0.4, carbs: 6.5, fiber: 2.8, sugar: 4.1, sodium: 3, potassium: 248 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  // Oils & Fats
  { id: 'item-020', name: 'Olive Oil, Extra Virgin', category: 'Oils & Fats', unit: 'L', costPerUnit: 8.99, nutrition: { kcal: 884, fat: 100, satfat: 13.8, sodium: 2 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-022', name: 'Tahini', category: 'Oils & Fats', unit: 'oz', costPerUnit: 0.44, description: '16 oz jar @ $6.99', nutrition: { kcal: 595, protein: 17, fat: 54, satfat: 7.6, carbs: 21.2, fiber: 9.3, sugar: 0.5, sodium: 115, calcium: 426, iron: 8.9, potassium: 414 }, allergens: ['Sesame'], createdAt: NOW, updatedAt: NOW },
  // Spices & Seasonings
  { id: 'item-030', name: 'Salt, Kosher', category: 'Spices & Seasonings', unit: 'oz', costPerUnit: 0.10, nutrition: { sodium: 38758 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-031', name: 'Black Pepper', category: 'Spices & Seasonings', unit: 'oz', costPerUnit: 0.75, nutrition: { kcal: 251, protein: 10.4, fat: 3.3, carbs: 63.9, fiber: 25.3, sodium: 20, calcium: 443, iron: 9.7, potassium: 1329 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-032', name: 'Cumin', category: 'Spices & Seasonings', unit: 'oz', costPerUnit: 1.12, nutrition: { kcal: 375, protein: 17.8, fat: 22.3, satfat: 1.5, carbs: 44.2, fiber: 10.5, sodium: 168, calcium: 931, iron: 66.4, potassium: 1788 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-033', name: 'Paprika', category: 'Spices & Seasonings', unit: 'oz', costPerUnit: 1.00, nutrition: { kcal: 282, protein: 14.1, fat: 12.9, satfat: 2.1, carbs: 53.9, fiber: 34.9, sodium: 68, calcium: 229, iron: 21.1, potassium: 2280 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-034', name: 'Sumac', category: 'Spices & Seasonings', unit: 'oz', costPerUnit: 2.00, nutrition: { kcal: 240, protein: 4, fat: 8, carbs: 44, fiber: 12, sodium: 10, potassium: 100 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  // Dairy — milk allergen
  { id: 'item-040', name: 'Feta Cheese', category: 'Dairy', unit: 'oz', costPerUnit: 0.75, nutrition: { kcal: 264, protein: 14.2, fat: 21.3, satfat: 14.9, cholesterol: 89, carbs: 4.1, sugar: 4.1, sodium: 917, calcium: 493, vitD: 0.4 }, allergens: ['Milk'], createdAt: NOW, updatedAt: NOW },
  // Canned & Jarred
  { id: 'item-050', name: 'Tomato Paste', category: 'Canned & Jarred', unit: 'oz', costPerUnit: 0.22, nutrition: { kcal: 82, protein: 4.3, fat: 0.5, carbs: 18.9, fiber: 4.1, sugar: 12.1, sodium: 39, potassium: 1014, iron: 4.1 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-051', name: 'Red Pepper Paste', category: 'Canned & Jarred', unit: 'oz', costPerUnit: 0.42, nutrition: { kcal: 80, protein: 2, fat: 3, carbs: 12, fiber: 2, sugar: 6, sodium: 500, potassium: 200 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-052', name: 'Apple Cider Vinegar', category: 'Canned & Jarred', unit: 'fl oz', costPerUnit: 0.22, nutrition: { kcal: 22, carbs: 0.9, sugar: 0.4, sodium: 5, potassium: 73 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  { id: 'item-053', name: 'Pomegranate Molasses', category: 'Canned & Jarred', unit: 'fl oz', costPerUnit: 0.60, nutrition: { kcal: 250, carbs: 65, sugar: 60, sodium: 15, potassium: 400 }, allergens: [], createdAt: NOW, updatedAt: NOW },
  // Nuts & Seeds — tree nut allergen
  { id: 'item-070', name: 'Pine Nuts', category: 'Nuts & Seeds', unit: 'oz', costPerUnit: 1.62, nutrition: { kcal: 673, protein: 13.7, fat: 68.4, satfat: 4.9, carbs: 13.1, fiber: 3.7, sodium: 2, potassium: 597, iron: 5.5 }, allergens: ['Tree Nuts'], createdAt: NOW, updatedAt: NOW },
  { id: 'item-071', name: 'Walnuts', category: 'Nuts & Seeds', unit: 'oz', costPerUnit: 0.53, nutrition: { kcal: 654, protein: 15.2, fat: 65.2, satfat: 6.1, carbs: 13.7, fiber: 6.7, sodium: 2, potassium: 441 }, allergens: ['Tree Nuts'], createdAt: NOW, updatedAt: NOW },
  // Packaging — non-edible, no nutrition
  { id: 'item-080', name: 'Deli Container 16oz', category: 'Packaging', unit: 'ea', costPerUnit: 0.09, description: 'Case of 500 @ $45', createdAt: NOW, updatedAt: NOW },
  { id: 'item-081', name: 'Lid for 16oz Container', category: 'Packaging', unit: 'ea', costPerUnit: 0.07, description: 'Case of 500 @ $35', createdAt: NOW, updatedAt: NOW },
  { id: 'item-082', name: 'Label, 2x3 inch', category: 'Packaging', unit: 'ea', costPerUnit: 0.025, description: 'Roll of 1000 @ $25', createdAt: NOW, updatedAt: NOW },
];

// ─── Recipes ──────────────────────────────────────────────────────────────

export const SEED_RECIPES: ProductionRecipe[] = [
  {
    id: 'recipe-001',
    name: 'Arabian Salad',
    description: 'Classic Middle Eastern chopped salad with fresh vegetables, herbs and lemon dressing',
    category: 'Salads',
    yieldQty: 5,
    yieldUnit: 'lb',
    sellPrice: 8.99,
    notes: 'Dice vegetables uniformly. Season to taste.',
    components: [
      { itemId: 'item-001', qty: 2,    unit: 'lb', notes: 'Diced 1/4 inch' },
      { itemId: 'item-002', qty: 1.5,  unit: 'lb', notes: 'Seeded, diced' },
      { itemId: 'item-003', qty: 0.5,  unit: 'lb', notes: 'Fine dice' },
      { itemId: 'item-006', qty: 0.5,  unit: 'lb', notes: 'Diced' },
      { itemId: 'item-010', qty: 0.5,  unit: 'ea', notes: 'Chopped' },
      { itemId: 'item-011', qty: 0.25, unit: 'ea', notes: 'Chopped' },
      { itemId: 'item-005', qty: 2,    unit: 'ea', notes: 'Juiced' },
      { itemId: 'item-020', qty: 0.06, unit: 'L',  notes: '60 ml' },
      { itemId: 'item-030', qty: 0.5,  unit: 'oz', notes: '' },
      { itemId: 'item-034', qty: 0.25, unit: 'oz', notes: '' },
    ],
    createdAt: NOW, updatedAt: NOW,
  },
  {
    id: 'recipe-002',
    name: 'Avocado Salad',
    description: 'Creamy avocado with fresh vegetables, cilantro and lime',
    category: 'Salads',
    yieldQty: 4,
    yieldUnit: 'lb',
    sellPrice: 10.99,
    notes: 'Cut avocado last to preserve color.',
    components: [
      { itemId: 'item-004', qty: 6,    unit: 'ea', notes: 'Cubed' },
      { itemId: 'item-001', qty: 1,    unit: 'lb', notes: 'Diced' },
      { itemId: 'item-003', qty: 0.25, unit: 'lb', notes: 'Fine dice' },
      { itemId: 'item-013', qty: 2,    unit: 'oz', notes: 'Minced' },
      { itemId: 'item-012', qty: 0.5,  unit: 'ea', notes: 'Chopped' },
      { itemId: 'item-005', qty: 2,    unit: 'ea', notes: 'Juiced' },
      { itemId: 'item-020', qty: 0.03, unit: 'L',  notes: '30 ml' },
      { itemId: 'item-030', qty: 0.5,  unit: 'oz', notes: '' },
      { itemId: 'item-032', qty: 0.15, unit: 'oz', notes: '' },
    ],
    createdAt: NOW, updatedAt: NOW,
  },
  {
    id: 'recipe-003',
    name: 'Beet Salad',
    description: 'Roasted beets with feta, walnuts and balsamic',
    category: 'Salads',
    yieldQty: 4,
    yieldUnit: 'lb',
    sellPrice: 11.99,
    notes: 'Roast beets at 400°F until tender.',
    components: [
      { itemId: 'item-007', qty: 3,    unit: 'lb',    notes: 'Roasted, cubed' },
      { itemId: 'item-040', qty: 4,    unit: 'oz',    notes: 'Crumbled' },
      { itemId: 'item-071', qty: 2,    unit: 'oz',    notes: 'Toasted' },
      { itemId: 'item-003', qty: 0.25, unit: 'lb',    notes: 'Sliced thin' },
      { itemId: 'item-020', qty: 0.045, unit: 'L',    notes: '45 ml' },
      { itemId: 'item-052', qty: 2,    unit: 'fl oz', notes: '' },
      { itemId: 'item-030', qty: 0.5,  unit: 'oz',    notes: '' },
      { itemId: 'item-031', qty: 0.1,  unit: 'oz',    notes: '' },
    ],
    createdAt: NOW, updatedAt: NOW,
  },
  {
    id: 'recipe-004',
    name: 'Bella Eggplant',
    description: 'Mediterranean roasted eggplant with tahini and pomegranate',
    category: 'Hot Sides',
    yieldQty: 3,
    yieldUnit: 'lb',
    sellPrice: 9.99,
    notes: 'Char eggplant directly on flame for smoky flavor.',
    components: [
      { itemId: 'item-008', qty: 3,    unit: 'lb',    notes: 'Roasted, mashed' },
      { itemId: 'item-022', qty: 3,    unit: 'oz',    notes: '' },
      { itemId: 'item-009', qty: 1,    unit: 'oz',    notes: 'Minced' },
      { itemId: 'item-005', qty: 1,    unit: 'ea',    notes: 'Juiced' },
      { itemId: 'item-053', qty: 1,    unit: 'fl oz', notes: 'Drizzle' },
      { itemId: 'item-020', qty: 0.03, unit: 'L',    notes: '30 ml' },
      { itemId: 'item-030', qty: 0.5,  unit: 'oz',    notes: '' },
      { itemId: 'item-033', qty: 0.15, unit: 'oz',    notes: '' },
      { itemId: 'item-070', qty: 1,    unit: 'oz',    notes: 'Garnish' },
    ],
    createdAt: NOW, updatedAt: NOW,
  },
  {
    id: 'recipe-005',
    name: 'Best Tomato Dip',
    description: 'Spicy tomato and red pepper dip with herbs',
    category: 'Dips',
    yieldQty: 2,
    yieldUnit: 'lb',
    sellPrice: 7.99,
    notes: 'Blend smooth or leave slightly chunky.',
    components: [
      { itemId: 'item-001', qty: 1.5,  unit: 'lb', notes: 'Blanched, peeled' },
      { itemId: 'item-051', qty: 3,    unit: 'oz', notes: '' },
      { itemId: 'item-050', qty: 2,    unit: 'oz', notes: '' },
      { itemId: 'item-009', qty: 0.5,  unit: 'oz', notes: 'Minced' },
      { itemId: 'item-013', qty: 1,    unit: 'oz', notes: 'Minced' },
      { itemId: 'item-020', qty: 0.03, unit: 'L',  notes: '30 ml' },
      { itemId: 'item-030', qty: 0.25, unit: 'oz', notes: '' },
      { itemId: 'item-032', qty: 0.1,  unit: 'oz', notes: '' },
      { itemId: 'item-033', qty: 0.15, unit: 'oz', notes: '' },
    ],
    createdAt: NOW, updatedAt: NOW,
  },
];

// ─── Products (finished SKUs) ─────────────────────────────────────────────

export const SEED_PRODUCTS: ProductionProduct[] = [
  {
    id: 'product-001',
    name: 'Arabian Salad — 16oz Retail',
    sku: 'AS-16OZ-001',
    description: 'Retail-ready 16oz deli container of Arabian Salad',
    category: 'Retail',
    recipeId: 'recipe-001',
    unitsPerBatch: 0.5,  // 16 oz = 1 lb (of recipe yield); 1 batch fills 10 containers
    packaging: [
      { itemId: 'item-080', qty: 1, unit: 'ea' },
      { itemId: 'item-081', qty: 1, unit: 'ea' },
      { itemId: 'item-082', qty: 1, unit: 'ea' },
    ],
    sellPrice: 8.99,
    createdAt: NOW, updatedAt: NOW,
  },
  {
    id: 'product-002',
    name: 'Beet Salad — 16oz Retail',
    sku: 'BS-16OZ-001',
    description: 'Retail-ready 16oz container of Beet Salad',
    category: 'Retail',
    recipeId: 'recipe-003',
    unitsPerBatch: 0.5,
    packaging: [
      { itemId: 'item-080', qty: 1, unit: 'ea' },
      { itemId: 'item-081', qty: 1, unit: 'ea' },
      { itemId: 'item-082', qty: 1, unit: 'ea' },
    ],
    sellPrice: 11.99,
    createdAt: NOW, updatedAt: NOW,
  },
];

// ─── Seed mechanism ───────────────────────────────────────────────────────

const SEEDED_FLAG_PREFIX = 'production_seeded_';

export function isTenantSeeded(slug: string): boolean {
  if (!slug) return false;
  return localStorage.getItem(`${SEEDED_FLAG_PREFIX}${slug}`) === 'true';
}

export function markTenantSeeded(slug: string): void {
  if (!slug) return;
  localStorage.setItem(`${SEEDED_FLAG_PREFIX}${slug}`, 'true');
}

export function clearTenantSeededFlag(slug: string): void {
  if (!slug) return;
  localStorage.removeItem(`${SEEDED_FLAG_PREFIX}${slug}`);
}
